import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CreateProgramLevelDto, UpdateProgramLevelDto } from '../types/dtos/program';
import { createProgramLevelService } from '../services/program-level.service';
import { PaginationParams } from '../types/common';

const PROGRAM_LEVELS_KEY = 'program-levels';

export const useProgramLevels = (
  organizationId: string,
  programId: string,
  params?: PaginationParams & { isActive?: boolean }
) => {
  const service = createProgramLevelService(organizationId, programId);

  return useQuery({
    queryKey: [PROGRAM_LEVELS_KEY, organizationId, programId, params],
    queryFn: () => service.findAll(params).then((res) => res.data),
    enabled: !!organizationId && !!programId,
  });
};

export const useProgramLevel = (
  organizationId: string,
  programId: string,
  levelId: string
) => {
  const service = createProgramLevelService(organizationId, programId);

  return useQuery({
    queryKey: [PROGRAM_LEVELS_KEY, organizationId, programId, levelId],
    queryFn: () => service.findById(levelId).then((res) => res.data),
    enabled: !!organizationId && !!programId && !!levelId,
  });
};

export const useCreateProgramLevel = (
  organizationId: string,
  programId: string
) => {
  const queryClient = useQueryClient();
  const service = createProgramLevelService(organizationId, programId);

  return useMutation({
    mutationFn: (data: CreateProgramLevelDto) => 
      service.create(data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROGRAM_LEVELS_KEY, organizationId, programId] });
    }
  });
};

export const useUpdateProgramLevel = (
  organizationId: string,
  programId: string
) => {
  const queryClient = useQueryClient();
  const service = createProgramLevelService(organizationId, programId);

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProgramLevelDto }) =>
      service.update(id, data).then((res) => res.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [PROGRAM_LEVELS_KEY, organizationId, programId] });
      queryClient.invalidateQueries({ queryKey: [PROGRAM_LEVELS_KEY, organizationId, programId, id] });
    }
  });
};

export const useDeleteProgramLevel = (
  organizationId: string,
  programId: string
) => {
  const queryClient = useQueryClient();
  const service = createProgramLevelService(organizationId, programId);

  return useMutation({
    mutationFn: (id: string) => service.delete(id).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROGRAM_LEVELS_KEY, organizationId, programId] });
    }
  });
};

